import { useFetchEQLFields } from '@modules/common/hooks';
import { IntegrationSourceDetailView } from '@modules/integrationsV2/components/IntegrationsContainer/IntegrationsSourceDetailView';
import { IntegrationsSourceList } from '@modules/integrationsV2/components/IntegrationsContainer/IntegrationsSourceList/IntegrationsSourceList';
import { SelectedAddIntergationChannelDrawer } from '@modules/integrationsV2/components/SelectedAddIntegrationChannelDrawer';
import { SelectedAddIntegrationFormDrawer } from '@modules/integrationsV2/components/SelectedAddIntegrationFormDrawer';
import { useSetMetadataFieldRelationsByTypeInStore } from '@modules/integrationsV2/hooks';
import { useIntegrationsStore } from '@modules/integrationsV2/store/integrationsStore';
import { getIntegrationSourcesGroupedByType } from '@modules/integrationsV2/utils';
import { Box, Stack, Typography } from '@mui/material';
import { IntegrationViewFragment } from 'generated/graphql';
import { useParams } from 'react-router-dom';

interface IntegrationsContainerContentProps {
  integrations: IntegrationViewFragment[];
}

export const IntegrationsContainerContent = ({
  integrations,
}: IntegrationsContainerContentProps) => {
  const { id: integrationIDFromURL } = useParams<{ id: string }>();

  const integrationSourcesGroupedByType =
    getIntegrationSourcesGroupedByType(integrations);

  const selectedIntegrationSourceOption = useIntegrationsStore(
    ({ view }) => view.selectedIntegrationSourceOption,
  );

  const getDefaultIntegrationSourceValue = () => {
    if (integrationIDFromURL) {
      const [eqlFieldsGroupID, integrationType] =
        decodeURIComponent(integrationIDFromURL).split('+');

      const integrationSourceFromURL = Object.values(
        integrationSourcesGroupedByType,
      )
        .flat()
        .find((integrationSource) => {
          return (
            integrationSource.eqlFieldsGroupID === eqlFieldsGroupID &&
            integrationSource.integrationType === integrationType
          );
        });

      return integrationSourceFromURL;
    }

    const defaultIntegrationSource = Object.values(
      integrationSourcesGroupedByType,
    ).find((integrationSources) => integrationSources.length > 0)?.[0];

    return defaultIntegrationSource;
  };

  const selectedIntegrationSource = (() => {
    if (!selectedIntegrationSourceOption) {
      return getDefaultIntegrationSourceValue();
    }

    const integrationSourcesForSelectedType =
      integrationSourcesGroupedByType[
        selectedIntegrationSourceOption.integrationType
      ];

    return integrationSourcesForSelectedType.find((integrationSource) => {
      const { integrationID, channel, integrationType } = integrationSource;

      const isComparableByIntegrationID =
        (integrationID && selectedIntegrationSourceOption.integrationID) ||
        Boolean(selectedIntegrationSourceOption.namespace);

      if (isComparableByIntegrationID) {
        return integrationID === selectedIntegrationSourceOption.integrationID;
      }

      return (
        channel.type === selectedIntegrationSourceOption.channelType &&
        integrationType === selectedIntegrationSourceOption.integrationType
      );
    });
  })();

  // Fetching with cache-and-network at root component level
  // So, we can use cache-first at other places to avoid unnecessary network calls
  useFetchEQLFields({
    fetchPolicy: 'cache-and-network',
  });

  useSetMetadataFieldRelationsByTypeInStore();

  return (
    <Stack direction="row" height="100%" width="100%" spacing={4} px={3.5}>
      <IntegrationsSourceList
        integrationSourcesGroupedByType={integrationSourcesGroupedByType}
        selected={selectedIntegrationSource}
      />
      {selectedIntegrationSource && (
        <IntegrationSourceDetailView
          integrations={integrations}
          integrationSource={selectedIntegrationSource}
        />
      )}
      {integrations.length === 0 && (
        <Box width="100%">
          <Typography
            variant="h6"
            textAlign="center"
            mt={40}
            color="text.secondary"
          >
            No integrations added yet
          </Typography>
        </Box>
      )}
      <SelectedAddIntegrationFormDrawer />
      <SelectedAddIntergationChannelDrawer />
    </Stack>
  );
};
